import * as React from 'react';
import { CalendarAssetCallbacksProvider } from 'owa-timestream-asset-sdk-calendars';
import { observer } from 'mobx-react-lite';
import { CalendarBoard, CalendarBoardHandle } from 'owa-calendar-board';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getFullCalendarEvent } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import { getCalendarEventWithId } from 'owa-calendar-events-store';
import { lazyOpenCalendarFullCompose } from 'owa-calendar-compose-form-lifecycle';
import { lazyOpenCalendarReadForm } from 'owa-calendar-read-form-lifecycle';
import EventScope from 'owa-service/lib/contract/EventScope';
import { getUserMailboxInfo } from 'owa-client-ids';
import { lazyOpenAddCalendarsPane } from 'owa-calendar-addcalendar';
import { useElementSizeTracker } from 'owa-react-hooks/lib/useElementSizeTracker';
import { getCalendarBoardAssetRegistry } from 'owa-calendar-board-assets';
import { focusCalendarBoard } from '../actions/focusCalendarBoard';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import type { ReadingPaneSection } from 'owa-calendar-helpers-types/lib/ReadingPaneSection';

import * as styles from './CalendarModuleBoard.scss';

export default observer(function CalendarModuleBoard() {
    const assetRegistry = getCalendarBoardAssetRegistry();

    const callbacks = {
        onOpenCalendarItem,
        onOpenCalendarItemDraft,
        onOpenFullCompose,
        onOpenEdit,
        onReviewProposedNewTime,
        onOpenAddCalendar,
    };

    const ref = React.useRef<HTMLDivElement>(null);
    const [rect] = useElementSizeTracker(ref);

    const boardChildRefHandler = React.useRef<CalendarBoardHandle>();

    useOrchestrator(
        focusCalendarBoard,
        () => {
            boardChildRefHandler.current?.focusBoardChild();
        },
        []
    );

    return (
        <div ref={ref} className={styles.moduleBoard}>
            <CalendarAssetCallbacksProvider value={callbacks}>
                <CalendarBoard
                    assetRegistry={assetRegistry}
                    canvasBounds={rect}
                    boardRefHandler={boardChildRefHandler}
                />
            </CalendarAssetCallbacksProvider>
        </div>
    );
});

const BoardCalendarAssetEntrySource = 'BoardCalendarAsset';

function onOpenAddCalendar() {
    lazyOpenAddCalendarsPane.importAndExecute('AssetCalendarPicker');
}

function onOpenCalendarItem(itemId: string, scrollToSection?: ReadingPaneSection) {
    lazyOpenCalendarReadForm.importAndExecute({
        itemId: { Id: itemId, mailboxInfo: getUserMailboxInfo() },
        entrySource: BoardCalendarAssetEntrySource,
        scrollToSectionOnInitialRender: scrollToSection,
    });
}

function onOpenCalendarItemDraft(itemId: string) {
    const event = getCalendarEventWithId(itemId);
    lazyOpenCalendarFullCompose.importAndExecute({
        fullComposeEntrySource: BoardCalendarAssetEntrySource,
        eventScope: event.IsRecurring ? EventScope.AllInstancesInSeries : EventScope.Default,
        eventUpdates: event,
    });
}

function onOpenFullCompose(event: Partial<CalendarEvent>) {
    lazyOpenCalendarFullCompose.importAndExecute({
        fullComposeEntrySource: BoardCalendarAssetEntrySource,
        eventUpdates: event,
    });
}

function onOpenEdit(
    event: Partial<CalendarEvent>,
    eventHasUpdates: boolean,
    eventScope: EventScope
) {
    lazyOpenCalendarFullCompose.importAndExecute({
        fullComposeEntrySource: BoardCalendarAssetEntrySource,
        eventScope: eventScope,
        eventUpdates: event,
        itemHasUpdates: eventHasUpdates,
    });
}

function onReviewProposedNewTime(itemId: string, newEvent?: Partial<CalendarEvent>) {
    const event = getFullCalendarEvent(null, itemId);
    lazyOpenCalendarFullCompose.importAndExecute({
        fullComposeEntrySource: BoardCalendarAssetEntrySource,
        eventUpdates: {
            ...event,
            ...newEvent,
        },
        itemHasUpdates: true,
    });
}
